// La grille de vente WAKEA. Source unique : le catalogue tarifaire v1.3.
// Elle sert à deux choses — classer un profil par famille, et calculer la marge
// implicite en régie, qui est le seul écart de taux qui veuille dire quelque chose.

export type Famille = 'conseil' | 'tech' | 'orga' | 'direction';

export interface Role { libelle: string; famille: Famille; regie: [number, number] }

export const GRILLE = {
  'associe_partner':        { libelle: 'Associé / Partner',                famille: 'direction', regie: [1900, 2300] },
  'directeur_programme':    { libelle: 'Directeur de mission / programme', famille: 'orga',      regie: [1200, 1500] },
  'fde_principal':          { libelle: 'FDE Principal Lead',               famille: 'tech',      regie: [1600, 2000] },
  'fde_senior':             { libelle: 'FDE senior',                       famille: 'tech',      regie: [1200, 1600] },
  'fde_confirme':           { libelle: 'FDE confirmé',                     famille: 'tech',      regie: [1000, 1200] },
  'expert_sectoriel':       { libelle: 'Expert sectoriel AI Augmented',    famille: 'conseil',   regie: [1200, 1800] },
  'architecte_ia':          { libelle: 'Architecte IA & plateforme',       famille: 'tech',      regie: [1300, 1550] },
  'gouvernance_ia':         { libelle: 'Gouvernance & conformité IA',      famille: 'orga',      regie: [1150, 1400] },
  'consultant_senior':      { libelle: 'Consultant senior stratégie',      famille: 'conseil',   regie: [1050, 1300] },
  'ingenieur_agents':       { libelle: 'Ingénieur agents / GenAI',         famille: 'tech',      regie: [1050, 1350] },
  // Ajoutés en septembre 2026, hors catalogue v1.3 : les fourchettes sont une
  // proposition à confirmer au prochain tirage du catalogue tarifaire.
  'cdp_ia':                 { libelle: 'Chef de projet IA',                famille: 'orga',      regie: [1000, 1250] },
  'ai_po':                  { libelle: 'AI Product Owner',                 famille: 'tech',      regie: [ 950, 1200] },
  'ai_pmo':                 { libelle: 'AI PMO',                           famille: 'orga',      regie: [ 850, 1100] },
  'data_scientist':         { libelle: 'Data scientist / ML',              famille: 'tech',      regie: [1000, 1250] },
  'ai_product_manager':     { libelle: 'AI Product Manager',               famille: 'tech',      regie: [1000, 1250] },
  'data_engineer':          { libelle: 'Data engineer',                    famille: 'tech',      regie: [ 900, 1150] },
  'lead_change':            { libelle: 'Lead change & adoption',           famille: 'orga',      regie: [1200, 1500] },
  'consultant_confirme':    { libelle: 'Consultant confirmé',              famille: 'conseil',   regie: [1000, 1300] },
  'analyste_ai_augmented':  { libelle: 'Analyste AI Augmented',            famille: 'conseil',   regie: [ 700,  900] },
} as const satisfies Record<string, Role>;

export type CleRole = keyof typeof GRILLE;
export const CLES_ROLES = Object.keys(GRILLE) as [CleRole, ...CleRole[]];

export const FAMILLES: Record<Famille, string> = {
  conseil: 'Conseil & métier',
  tech: 'Tech & IA',
  orga: 'Organisation & programme',
  direction: 'Direction',
};

export const clesDeFamille = (f: Famille) =>
  CLES_ROLES.filter((c) => GRILLE[c].famille === f);

/**
 * Équivalent en taux journalier d'une rémunération annuelle brute. Le diviseur est la
 * règle du cabinet : 40 000 € bruts se lisent 400 € par jour. Il s'écrit ici, une fois,
 * et tout ce qui compare un salarié à un indépendant passe par cette fonction.
 */
export const DIVISEUR_SALAIRE = 100;

export const tjEquivalent = (salaireBrut: number | null | undefined): number | null =>
  salaireBrut == null ? null : Math.round(salaireBrut / DIVISEUR_SALAIRE);

/**
 * Ce que coûte une journée de cette personne, quelle que soit la forme du contrat :
 * son taux s'il en a dit un, sinon l'équivalent du bas de sa fourchette de salaire.
 * C'est la seule base sur laquelle une marge veuille dire quelque chose.
 */
export const coutJournalier = (
  tjm: number | null | undefined,
  salaireMin: number | null | undefined,
  salaireMax: number | null | undefined,
): number | null => tjm ?? tjEquivalent(salaireMin ?? salaireMax);

/** Marge implicite en régie, en points de pourcentage, au milieu de la fourchette de vente. */
export function margeRegie(cle: string | null, tjmAchat: number | null): number | null {
  if (!cle || tjmAchat == null || !(cle in GRILLE)) return null;
  const [bas, haut] = GRILLE[cle as CleRole].regie;
  return Math.round((1 - tjmAchat / ((bas + haut) / 2)) * 100);
}
